import { ButtonInline } from '@remote-com/norma/core/button';
import { Fragment, useState } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import { DocumentHead } from 'remote-com-shared/components/DocumentHead';
import { Hero } from 'remote-com-shared/components/Hero';
import { isDefaultLocaleLanguage } from 'remote-com-shared/domains/locales/helpers';
import styled from 'styled-components';

// According to the official documentation the import below
// is required to display correctly annotations (e.g. links).
// For more details, go to https://github.com/wojtekmaj/react-pdf#support-for-annotations
import 'react-pdf/dist/esm/Page/AnnotationLayer.css';
import 'react-pdf/dist/esm/Page/TextLayer.css';

import Loader from '@/src/components/Loader';
import { querySharedServices } from '@/src/domains/shared/services';
import { formatDate } from '@/src/domains/termsOfService/helpers';
import { getAllTermsOfService } from '@/src/domains/termsOfService/services';

// eslint-disable-next-line import/no-unresolved, import/no-absolute-path, import/extensions
// import workerSrc from '/pdf-worker';

// import pdf worker as a url, see `next.config.js` and `pdf-worker.js`
pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/legacy/build/pdf.worker.min.mjs`;

const ContentContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  max-width: 960px;
  margin: 0 auto;
`;

const DocumentContainer = styled.div`
  padding: 15px 0;
  margin: 0;
  canvas {
    max-width: 100%;
    height: auto !important;
  }

  .annotationLayer {
    // Annotation layer contains links and other annotations
    // It needs to match the sizing of the canvas as defined above
    width: 100% !important;
    height: 100% !important;
  }
`;

const StyledPage = styled(Page)`
  margin-bottom: 20px;
`;

const DocumentListContainer = styled.div`
  padding: 15px;
  align-self: flex-start;
`;

const DocumentList = styled.ul`
  list-style: none;
  padding-left: 0;
  align-self: flex-start;
`;

const HorizontalRule = styled.hr`
  margin: 20px 0;
  background-color: var(--colors-geyser);
  height: 1px;
  border: none;
  width: 100%;
`;

export default function TermsOfServiceArchive({ currentDocument, documents = [] }) {
  const lastUpdatedDate = currentDocument ? formatDate(currentDocument.revisionDatetime) : 'Never';

  const [totalPages, setTotalPages] = useState(null);

  function onDocumentLoadSuccess({ numPages }) {
    setTotalPages(numPages);
  }

  function showAllPages() {
    return Array(totalPages)
      .fill()
      .map((_, i) => (
        <Fragment key={i + 1}>
          <StyledPage pageNumber={i + 1} scale={1.5} />
        </Fragment>
      ));
  }

  return (
    <>
      <DocumentHead document={{ seo_title: 'Terms of service archive' }} />
      <Hero
        title="Terms of service archive"
        description={`Last updated: ${lastUpdatedDate}`}
        preview="60vh"
      />

      <ContentContainer>
        <h1 style={{ alignSelf: 'flex-start' }}>Current terms of service</h1>
        <DocumentContainer>
          <Document
            file={currentDocument?.document?.url}
            onLoadSuccess={onDocumentLoadSuccess}
            loading={<Loader />}
          >
            {showAllPages()}
          </Document>
        </DocumentContainer>
        <HorizontalRule />
        <DocumentListContainer>
          {documents?.length > 0 && (
            <h2 style={{ alignSelf: 'flex-start' }}>Terms of service revisions</h2>
          )}
          {documents.map((document, index) => (
            <DocumentList key={index}>
              <li>
                <ButtonInline href={document.document?.url || ''} target="_blank" rel="noreferrer">
                  View Terms of Service ({`updated ${formatDate(document.revisionDatetime)}`})
                </ButtonInline>
              </li>
            </DocumentList>
          ))}
        </DocumentListContainer>
      </ContentContainer>
    </>
  );
}

export async function getStaticProps({ previewData = {}, locale, defaultLocale }) {
  if (!isDefaultLocaleLanguage(locale, defaultLocale)) {
    return { notFound: true };
  }

  const documents = await getAllTermsOfService({ previewData, locale });
  const currentDocument = documents[0];

  const sharedServices = await querySharedServices({ previewData, locale });

  return {
    props: {
      currentDocument,
      documents,
      sharedServices,
    },
  };
}
